import { Course } from '../courses/types';

export const UNITY_GAME_DEVELOPMENT: Course = {
  id: 'unity-game-development',
  title: 'Unity Game Development',
  description: 'Master game development with Unity engine, from 2D platformers to 3D adventures.',
  level: 'intermediate',
  duration: '8-10 weeks',
  estimatedHours: 40,
  tags: ['Unity', 'C#', 'Game Development', '3D Graphics', 'Animation'],
  prerequisites: ['Basic C# programming knowledge', 'Object-oriented programming concepts'],
  learningOutcomes: [
    'Build complete 2D and 3D games in Unity',
    'Master Unity Editor and workflow',
    'Implement game mechanics and physics',
    'Create interactive UI and game states',
    'Optimize games for performance',
    'Deploy games to multiple platforms'
  ],
  modules: [
    {
      id: 'unity-fundamentals',
      title: 'Unity Fundamentals',
      description: 'Get started with Unity engine, editor interface, and basic concepts.',
      duration: '2 hours',
      difficulty: 'beginner',
      content: `
# Unity Fundamentals

## Overview
Unity is a powerful cross-platform game engine used by developers worldwide to create 2D, 3D, VR, and AR games.

## Unity Editor Interface

### 1. Main Components
- **Scene View**: 3D workspace for building your game world
- **Hierarchy**: Shows all GameObjects in your scene
- **Inspector**: Properties and components of selected objects
- **Project**: Asset management and file organization
- **Console**: Debug messages and error logs

### 2. Core Concepts
- **GameObjects**: Basic building blocks of Unity scenes
- **Components**: Functionality attached to GameObjects
- **Scripts**: C# code that defines behavior
- **Prefabs**: Reusable GameObject templates

## Your First Unity Project

\`\`\`csharp
using UnityEngine;

public class HelloUnity : MonoBehaviour
{
    void Start()
    {
        Debug.Log("Welcome to Unity Game Development!");
        
        // Get component reference
        Rigidbody rb = GetComponent<Rigidbody>();
        
        // Add force to the object
        if (rb != null)
        {
            rb.AddForce(Vector3.up * 10f, ForceMode.Impulse);
        }
    }
    
    void Update()
    {
        // This runs every frame
        if (Input.GetKeyDown(KeyCode.Space))
        {
            transform.Rotate(0, 90, 0);
        }
    }
}
\`\`\`

## Asset Workflow
- **Importing**: Drag assets into Project window
- **Materials**: Control object appearance
- **Textures**: Surface details and colors
- **Audio**: Sound effects and music integration
      `,
      learningObjectives: [
        'Navigate Unity Editor interface',
        'Understand GameObjects and Components',
        'Write basic C# scripts for Unity',
        'Import and organize project assets'
      ]
    },
    {
      id: 'unity-2d-game',
      title: '2D Game Development',
      description: 'Create engaging 2D games with sprites, animations, and physics.',
      duration: '3 hours',
      difficulty: 'intermediate',
      content: `
# 2D Game Development in Unity

## Setting Up 2D Projects

### Project Configuration
- Set Project to 2D mode
- Configure Camera for orthographic projection
- Import 2D sprites and textures

### Sprite Management
\`\`\`csharp
using UnityEngine;

public class SpriteController : MonoBehaviour
{
    public SpriteRenderer spriteRenderer;
    public Sprite[] characterSprites;
    private int currentSpriteIndex = 0;
    
    void Start()
    {
        spriteRenderer = GetComponent<SpriteRenderer>();
    }
    
    public void ChangeSprite()
    {
        currentSpriteIndex = (currentSpriteIndex + 1) % characterSprites.Length;
        spriteRenderer.sprite = characterSprites[currentSpriteIndex];
    }
}
\`\`\`

## 2D Physics and Collisions

### Rigidbody2D Setup
\`\`\`csharp
public class PlayerController2D : MonoBehaviour
{
    public float moveSpeed = 5f;
    public float jumpForce = 10f;
    
    private Rigidbody2D rb;
    private bool isGrounded = false;
    
    void Start()
    {
        rb = GetComponent<Rigidbody2D>();
    }
    
    void Update()
    {
        // Horizontal movement
        float horizontal = Input.GetAxis("Horizontal");
        rb.velocity = new Vector2(horizontal * moveSpeed, rb.velocity.y);
        
        // Jumping
        if (Input.GetKeyDown(KeyCode.Space) && isGrounded)
        {
            rb.AddForce(Vector2.up * jumpForce, ForceMode2D.Impulse);
        }
    }
    
    void OnCollisionEnter2D(Collision2D collision)
    {
        if (collision.gameObject.CompareTag("Ground"))
        {
            isGrounded = true;
        }
    }
    
    void OnCollisionExit2D(Collision2D collision)
    {
        if (collision.gameObject.CompareTag("Ground"))
        {
            isGrounded = false;
        }
    }
}
\`\`\`

## 2D Animation System

### Animator Controller
\`\`\`csharp
public class CharacterAnimator : MonoBehaviour
{
    private Animator animator;
    private Rigidbody2D rb;
    
    void Start()
    {
        animator = GetComponent<Animator>();
        rb = GetComponent<Rigidbody2D>();
    }
    
    void Update()
    {
        // Set animation parameters
        animator.SetFloat("Speed", Mathf.Abs(rb.velocity.x));
        animator.SetBool("IsGrounded", isGrounded);
        
        if (Input.GetKeyDown(KeyCode.Space) && isGrounded)
        {
            animator.SetTrigger("Jump");
        }
    }
}
\`\`\`

## Parallax Scrolling
\`\`\`csharp
public class ParallaxBackground : MonoBehaviour
{
    public Transform cameraTransform;
    public float parallaxEffectMultiplier;
    
    private Vector3 lastCameraPosition;
    
    void Start()
    {
        lastCameraPosition = cameraTransform.position;
    }
    
    void LateUpdate()
    {
        Vector3 deltaMovement = cameraTransform.position - lastCameraPosition;
        transform.position += deltaMovement * parallaxEffectMultiplier;
        lastCameraPosition = cameraTransform.position;
    }
}
\`\`\`
      `,
      learningObjectives: [
        'Set up 2D Unity projects',
        'Implement 2D character movement',
        'Create sprite animations',
        'Add parallax scrolling effects'
      ]
    },
    {
      id: 'unity-3d-game',
      title: '3D Game Development',
      description: 'Build immersive 3D games with advanced graphics and mechanics.',
      duration: '4 hours',
      difficulty: 'advanced',
      content: `
# 3D Game Development in Unity

## 3D Environment Setup

### Terrain Creation
\`\`\`csharp
using UnityEngine;

public class TerrainGenerator : MonoBehaviour
{
    public Terrain terrain;
    public int heightmapResolution = 513;
    public float heightScale = 20f;
    
    void Start()
    {
        GenerateTerrain();
    }
    
    void GenerateTerrain()
    {
        TerrainData terrainData = terrain.terrainData;
        terrainData.heightmapResolution = heightmapResolution;
        
        float[,] heights = new float[heightmapResolution, heightmapResolution];
        
        for (int x = 0; x < heightmapResolution; x++)
        {
            for (int y = 0; y < heightmapResolution; y++)
            {
                float height = Mathf.PerlinNoise(x * 0.01f, y * 0.01f) * heightScale;
                heights[x, y] = height / terrainData.size.y;
            }
        }
        
        terrainData.SetHeights(0, 0, heights);
    }
}
\`\`\`

## First Person Controller
\`\`\`csharp
public class FirstPersonController : MonoBehaviour
{
    public float walkSpeed = 6f;
    public float runSpeed = 12f;
    public float jumpPower = 7f;
    public float mouseSensitivity = 2f;
    
    public Camera playerCamera;
    public float lookXLimit = 45f;
    
    Vector3 moveDirection = Vector3.zero;
    float rotationX = 0;
    
    public bool canMove = true;
    
    CharacterController characterController;
    
    void Start()
    {
        characterController = GetComponent<CharacterController>();
        Cursor.lockState = CursorLockMode.Locked;
        Cursor.visible = false;
    }
    
    void Update()
    {
        // Player movement
        Vector3 forward = transform.TransformDirection(Vector3.forward);
        Vector3 right = transform.TransformDirection(Vector3.right);
        
        bool isRunning = Input.GetKey(KeyCode.LeftShift);
        float curSpeedX = canMove ? (isRunning ? runSpeed : walkSpeed) * Input.GetAxis("Vertical") : 0;
        float curSpeedY = canMove ? (isRunning ? runSpeed : walkSpeed) * Input.GetAxis("Horizontal") : 0;
        float movementDirectionY = moveDirection.y;
        moveDirection = (forward * curSpeedX) + (right * curSpeedY);
        
        // Jumping
        if (Input.GetButton("Jump") && canMove && characterController.isGrounded)
        {
            moveDirection.y = jumpPower;
        }
        else
        {
            moveDirection.y = movementDirectionY;
        }
        
        // Apply gravity
        if (!characterController.isGrounded)
        {
            moveDirection.y -= 20f * Time.deltaTime;
        }
        
        // Move the controller
        characterController.Move(moveDirection * Time.deltaTime);
        
        // Player and Camera rotation
        if (canMove)
        {
            rotationX += -Input.GetAxis("Mouse Y") * mouseSensitivity;
            rotationX = Mathf.Clamp(rotationX, -lookXLimit, lookXLimit);
            playerCamera.transform.localRotation = Quaternion.Euler(rotationX, 0, 0);
            transform.rotation *= Quaternion.Euler(0, Input.GetAxis("Mouse X") * mouseSensitivity, 0);
        }
    }
}
\`\`\`

## Lighting and Shadows
\`\`\`csharp
public class DynamicLighting : MonoBehaviour
{
    public Light sunLight;
    public Gradient sunColor;
    public AnimationCurve sunIntensity;
    
    [Range(0, 24)]
    public float timeOfDay = 12f;
    public float dayDuration = 120f; // seconds
    
    void Update()
    {
        timeOfDay += Time.deltaTime / dayDuration * 24f;
        if (timeOfDay >= 24f) timeOfDay = 0f;
        
        UpdateLighting();
    }
    
    void UpdateLighting()
    {
        float normalizedTime = timeOfDay / 24f;
        
        // Rotate sun based on time
        float sunAngle = normalizedTime * 360f - 90f;
        sunLight.transform.rotation = Quaternion.Euler(sunAngle, 170f, 0f);
        
        // Update sun color and intensity
        sunLight.color = sunColor.Evaluate(normalizedTime);
        sunLight.intensity = sunIntensity.Evaluate(normalizedTime);
        
        // Enable/disable shadows based on sun intensity
        sunLight.shadows = sunLight.intensity > 0.1f ? LightShadows.Soft : LightShadows.None;
    }
}
\`\`\`

## 3D Audio Implementation
\`\`\`csharp
public class AudioManager : MonoBehaviour
{
    public AudioSource musicSource;
    public AudioSource sfxSource;
    
    public AudioClip[] backgroundMusic;
    public AudioClip[] soundEffects;
    
    private Dictionary<string, AudioClip> audioClips;
    
    void Start()
    {
        audioClips = new Dictionary<string, AudioClip>();
        
        // Load all audio clips
        foreach (AudioClip clip in soundEffects)
        {
            audioClips[clip.name] = clip;
        }
        
        PlayRandomMusic();
    }
    
    public void PlaySFX(string clipName)
    {
        if (audioClips.ContainsKey(clipName))
        {
            sfxSource.PlayOneShot(audioClips[clipName]);
        }
    }
    
    public void PlayRandomMusic()
    {
        if (backgroundMusic.Length > 0)
        {
            AudioClip randomClip = backgroundMusic[Random.Range(0, backgroundMusic.Length)];
            musicSource.clip = randomClip;
            musicSource.Play();
        }
    }
}
\`\`\`
      `,
      learningObjectives: [
        'Create 3D environments and terrain',
        'Implement first-person character controllers',
        'Set up dynamic lighting systems',
        'Integrate 3D audio and sound effects'
      ]
    },
    {
      id: 'unity-game-mechanics',
      title: 'Advanced Game Mechanics',
      description: 'Implement complex game systems like inventory, combat, and AI.',
      duration: '3 hours',
      difficulty: 'advanced',
      content: `
# Advanced Game Mechanics

## Inventory System
\`\`\`csharp
[System.Serializable]
public class InventoryItem
{
    public string itemName;
    public Sprite itemIcon;
    public int quantity;
    public string description;
    
    public InventoryItem(string name, Sprite icon, int qty, string desc)
    {
        itemName = name;
        itemIcon = icon;
        quantity = qty;
        description = desc;
    }
}

public class InventoryManager : MonoBehaviour
{
    public List<InventoryItem> inventory = new List<InventoryItem>();
    public Transform inventoryUI;
    public GameObject inventorySlotPrefab;
    
    void Start()
    {
        UpdateInventoryUI();
    }
    
    public void AddItem(InventoryItem newItem)
    {
        // Check if item already exists
        InventoryItem existingItem = inventory.Find(item => item.itemName == newItem.itemName);
        
        if (existingItem != null)
        {
            existingItem.quantity += newItem.quantity;
        }
        else
        {
            inventory.Add(newItem);
        }
        
        UpdateInventoryUI();
    }
    
    public void RemoveItem(string itemName, int quantity = 1)
    {
        InventoryItem item = inventory.Find(i => i.itemName == itemName);
        
        if (item != null)
        {
            item.quantity -= quantity;
            if (item.quantity <= 0)
            {
                inventory.Remove(item);
            }
        }
        
        UpdateInventoryUI();
    }
    
    void UpdateInventoryUI()
    {
        // Clear existing UI
        foreach (Transform child in inventoryUI)
        {
            Destroy(child.gameObject);
        }
        
        // Create UI for each item
        foreach (InventoryItem item in inventory)
        {
            GameObject slot = Instantiate(inventorySlotPrefab, inventoryUI);
            InventorySlot slotScript = slot.GetComponent<InventorySlot>();
            slotScript.SetItem(item);
        }
    }
}
\`\`\`

## Combat System
\`\`\`csharp
public class CombatSystem : MonoBehaviour
{
    [Header("Combat Stats")]
    public float maxHealth = 100f;
    public float currentHealth;
    public float attackDamage = 25f;
    public float attackRange = 2f;
    public float attackCooldown = 1f;
    
    private float lastAttackTime;
    private Animator animator;
    
    public HealthBar healthBar;
    
    void Start()
    {
        currentHealth = maxHealth;
        animator = GetComponent<Animator>();
        healthBar.SetMaxHealth(maxHealth);
    }
    
    void Update()
    {
        if (Input.GetMouseButtonDown(0) && CanAttack())
        {
            Attack();
        }
    }
    
    bool CanAttack()
    {
        return Time.time - lastAttackTime >= attackCooldown;
    }
    
    void Attack()
    {
        lastAttackTime = Time.time;
        animator.SetTrigger("Attack");
        
        // Detect enemies in range
        Collider[] enemies = Physics.OverlapSphere(transform.position, attackRange);
        
        foreach (Collider enemy in enemies)
        {
            if (enemy.CompareTag("Enemy"))
            {
                CombatSystem enemyCombat = enemy.GetComponent<CombatSystem>();
                if (enemyCombat != null)
                {
                    enemyCombat.TakeDamage(attackDamage);
                }
            }
        }
    }
    
    public void TakeDamage(float damage)
    {
        currentHealth -= damage;
        healthBar.SetHealth(currentHealth);
        
        animator.SetTrigger("Hit");
        
        if (currentHealth <= 0)
        {
            Die();
        }
    }
    
    void Die()
    {
        animator.SetTrigger("Death");
        
        // Disable combat and movement
        GetComponent<Collider>().enabled = false;
        this.enabled = false;
        
        // Award experience, drop loot, etc.
        GameManager.Instance.OnEnemyDefeated(gameObject);
    }
    
    void OnDrawGizmosSelected()
    {
        Gizmos.color = Color.red;
        Gizmos.DrawWireSphere(transform.position, attackRange);
    }
}
\`\`\`

## AI Behavior System
\`\`\`csharp
using UnityEngine.AI;

public enum AIState
{
    Idle,
    Patrol,
    Chase,
    Attack,
    Return
}

public class EnemyAI : MonoBehaviour
{
    [Header("AI Settings")]
    public AIState currentState = AIState.Idle;
    public float detectionRadius = 10f;
    public float attackRadius = 2f;
    public float patrolRadius = 15f;
    
    [Header("Patrol Points")]
    public Transform[] patrolPoints;
    private int currentPatrolIndex = 0;
    
    private NavMeshAgent navAgent;
    private Transform player;
    private Vector3 startPosition;
    private CombatSystem combatSystem;
    
    void Start()
    {
        navAgent = GetComponent<NavMeshAgent>();
        combatSystem = GetComponent<CombatSystem>();
        startPosition = transform.position;
        player = GameObject.FindGameObjectWithTag("Player").transform;
        
        SetState(AIState.Patrol);
    }
    
    void Update()
    {
        switch (currentState)
        {
            case AIState.Idle:
                HandleIdleState();
                break;
            case AIState.Patrol:
                HandlePatrolState();
                break;
            case AIState.Chase:
                HandleChaseState();
                break;
            case AIState.Attack:
                HandleAttackState();
                break;
            case AIState.Return:
                HandleReturnState();
                break;
        }
        
        CheckForPlayer();
    }
    
    void SetState(AIState newState)
    {
        currentState = newState;
        
        switch (newState)
        {
            case AIState.Patrol:
                if (patrolPoints.Length > 0)
                {
                    navAgent.SetDestination(patrolPoints[currentPatrolIndex].position);
                }
                break;
            case AIState.Chase:
                navAgent.speed = 6f; // Increase speed for chasing
                break;
            case AIState.Return:
                navAgent.SetDestination(startPosition);
                navAgent.speed = 3.5f; // Normal speed
                break;
        }
    }
    
    void HandlePatrolState()
    {
        if (!navAgent.pathPending && navAgent.remainingDistance < 0.5f)
        {
            currentPatrolIndex = (currentPatrolIndex + 1) % patrolPoints.Length;
            navAgent.SetDestination(patrolPoints[currentPatrolIndex].position);
        }
    }
    
    void HandleChaseState()
    {
        if (player != null)
        {
            navAgent.SetDestination(player.position);
            
            float distanceToPlayer = Vector3.Distance(transform.position, player.position);
            if (distanceToPlayer <= attackRadius)
            {
                SetState(AIState.Attack);
            }
        }
    }
    
    void HandleAttackState()
    {
        navAgent.SetDestination(transform.position); // Stop moving
        
        // Look at player
        Vector3 lookDirection = (player.position - transform.position).normalized;
        lookDirection.y = 0;
        transform.rotation = Quaternion.LookRotation(lookDirection);
        
        // Attack logic handled by CombatSystem
        float distanceToPlayer = Vector3.Distance(transform.position, player.position);
        if (distanceToPlayer > attackRadius)
        {
            SetState(AIState.Chase);
        }
    }
    
    void CheckForPlayer()
    {
        float distanceToPlayer = Vector3.Distance(transform.position, player.position);
        
        if (currentState != AIState.Chase && currentState != AIState.Attack)
        {
            if (distanceToPlayer <= detectionRadius)
            {
                SetState(AIState.Chase);
            }
        }
        else if (distanceToPlayer > detectionRadius * 1.5f)
        {
            SetState(AIState.Return);
        }
    }
    
    void OnDrawGizmosSelected()
    {
        Gizmos.color = Color.yellow;
        Gizmos.DrawWireSphere(transform.position, detectionRadius);
        
        Gizmos.color = Color.red;
        Gizmos.DrawWireSphere(transform.position, attackRadius);
    }
}
\`\`\`
      `,
      learningObjectives: [
        'Build comprehensive inventory systems',
        'Create engaging combat mechanics',
        'Implement AI behavior patterns',
        'Design complex game state management'
      ]
    },
    {
      id: 'unity-optimization',
      title: 'Performance Optimization',
      description: 'Optimize Unity games for performance across multiple platforms.',
      duration: '2 hours',
      difficulty: 'advanced',
      content: `
# Unity Performance Optimization

## Profiling and Analysis

### Unity Profiler
\`\`\`csharp
public class PerformanceMonitor : MonoBehaviour
{
    [Header("Performance Metrics")]
    public bool showFPS = true;
    public bool showMemory = true;
    
    private float deltaTime = 0.0f;
    private GUIStyle style;
    
    void Start()
    {
        // Enable deep profiling for detailed analysis
        #if UNITY_EDITOR
        UnityEngine.Profiling.Profiler.enableBinaryLog = true;
        UnityEngine.Profiling.Profiler.enabled = true;
        #endif
        
        style = new GUIStyle();
        style.alignment = TextAnchor.UpperLeft;
        style.fontSize = 24;
        style.normal.textColor = Color.white;
    }
    
    void Update()
    {
        deltaTime += (Time.unscaledDeltaTime - deltaTime) * 0.1f;
    }
    
    void OnGUI()
    {
        if (showFPS)
        {
            float msec = deltaTime * 1000.0f;
            float fps = 1.0f / deltaTime;
            string text = string.Format("{0:0.0} ms ({1:0.} fps)", msec, fps);
            
            Rect rect = new Rect(10, 10, 300, 30);
            GUI.Label(rect, text, style);
        }
        
        if (showMemory)
        {
            long memoryUsage = System.GC.GetTotalMemory(false);
            string memText = "Memory: " + (memoryUsage / 1024 / 1024) + " MB";
            
            Rect memRect = new Rect(10, 40, 300, 30);
            GUI.Label(memRect, memText, style);
        }
    }
}
\`\`\`

## Object Pooling
\`\`\`csharp
public class ObjectPool : MonoBehaviour
{
    [System.Serializable]
    public class Pool
    {
        public string tag;
        public GameObject prefab;
        public int size;
    }
    
    public List<Pool> pools;
    public Dictionary<string, Queue<GameObject>> poolDictionary;
    
    void Start()
    {
        poolDictionary = new Dictionary<string, Queue<GameObject>>();
        
        foreach (Pool pool in pools)
        {
            Queue<GameObject> objectPool = new Queue<GameObject>();
            
            for (int i = 0; i < pool.size; i++)
            {
                GameObject obj = Instantiate(pool.prefab);
                obj.SetActive(false);
                objectPool.Enqueue(obj);
            }
            
            poolDictionary.Add(pool.tag, objectPool);
        }
    }
    
    public GameObject SpawnFromPool(string tag, Vector3 position, Quaternion rotation)
    {
        if (!poolDictionary.ContainsKey(tag))
        {
            Debug.LogWarning("Pool with tag " + tag + " doesn't exist.");
            return null;
        }
        
        GameObject objectToSpawn = poolDictionary[tag].Dequeue();
        
        objectToSpawn.SetActive(true);
        objectToSpawn.transform.position = position;
        objectToSpawn.transform.rotation = rotation;
        
        IPooledObject pooledObj = objectToSpawn.GetComponent<IPooledObject>();
        pooledObj?.OnObjectSpawn();
        
        poolDictionary[tag].Enqueue(objectToSpawn);
        
        return objectToSpawn;
    }
}

public interface IPooledObject
{
    void OnObjectSpawn();
}
\`\`\`

## LOD (Level of Detail) System
\`\`\`csharp
public class DynamicLOD : MonoBehaviour
{
    [Header("LOD Settings")]
    public GameObject[] lodModels;
    public float[] lodDistances;
    
    private Camera mainCamera;
    private int currentLOD = -1;
    
    void Start()
    {
        mainCamera = Camera.main;
        
        // Disable all LODs initially
        foreach (GameObject lod in lodModels)
        {
            lod.SetActive(false);
        }
    }
    
    void Update()
    {
        UpdateLOD();
    }
    
    void UpdateLOD()
    {
        float distance = Vector3.Distance(transform.position, mainCamera.transform.position);
        int newLOD = GetLODLevel(distance);
        
        if (newLOD != currentLOD)
        {
            // Disable current LOD
            if (currentLOD >= 0 && currentLOD < lodModels.Length)
            {
                lodModels[currentLOD].SetActive(false);
            }
            
            // Enable new LOD
            if (newLOD >= 0 && newLOD < lodModels.Length)
            {
                lodModels[newLOD].SetActive(true);
            }
            
            currentLOD = newLOD;
        }
    }
    
    int GetLODLevel(float distance)
    {
        for (int i = 0; i < lodDistances.Length; i++)
        {
            if (distance <= lodDistances[i])
            {
                return i;
            }
        }
        
        return lodDistances.Length - 1;
    }
}
\`\`\`

## Texture and Asset Optimization
\`\`\`csharp
public class TextureOptimizer : MonoBehaviour
{
    [Header("Texture Settings")]
    public Texture2D[] textures;
    
    void Start()
    {
        OptimizeTextures();
    }
    
    void OptimizeTextures()
    {
        foreach (Texture2D texture in textures)
        {
            // Enable texture compression
            #if UNITY_EDITOR
            string texturePath = UnityEditor.AssetDatabase.GetAssetPath(texture);
            UnityEditor.TextureImporter textureImporter = 
                UnityEditor.AssetImporter.GetAtPath(texturePath) as UnityEditor.TextureImporter;
            
            if (textureImporter != null)
            {
                // Set compression format based on platform
                textureImporter.textureCompression = UnityEditor.TextureImporterCompression.Compressed;
                textureImporter.compressionQuality = 50; // Adjust based on quality needs
                
                // Generate mipmaps for textures that scale
                textureImporter.mipmapEnabled = true;
                
                // Set max texture size
                textureImporter.maxTextureSize = 1024; // Adjust based on needs
                
                UnityEditor.AssetDatabase.ImportAsset(texturePath);
            }
            #endif
        }
    }
}

// Async asset loading for large assets
public class AssetLoader : MonoBehaviour
{
    public async void LoadAssetAsync<T>(string assetPath) where T : UnityEngine.Object
    {
        var request = Resources.LoadAsync<T>(assetPath);
        
        while (!request.isDone)
        {
            await System.Threading.Tasks.Task.Yield();
        }
        
        T loadedAsset = request.asset as T;
        
        if (loadedAsset != null)
        {
            // Use the loaded asset
            Debug.Log($"Successfully loaded: {loadedAsset.name}");
        }
    }
}
\`\`\`

## Platform-Specific Optimizations
\`\`\`csharp
public class PlatformOptimizer : MonoBehaviour
{
    void Start()
    {
        OptimizeForPlatform();
    }
    
    void OptimizeForPlatform()
    {
        #if UNITY_ANDROID || UNITY_IOS
            // Mobile optimizations
            QualitySettings.vSyncCount = 0; // Disable VSync for better performance
            Application.targetFrameRate = 60;
            
            // Reduce shadow distance and quality
            QualitySettings.shadowDistance = 50f;
            QualitySettings.shadowResolution = ShadowResolution.Low;
            
        #elif UNITY_STANDALONE_WIN || UNITY_STANDALONE_OSX
            // Desktop optimizations
            QualitySettings.vSyncCount = 1;
            Application.targetFrameRate = -1; // Unlimited framerate
            
            // Higher quality settings for desktop
            QualitySettings.shadowDistance = 150f;
            QualitySettings.shadowResolution = ShadowResolution.High;
            
        #elif UNITY_WEBGL
            // WebGL optimizations
            Application.targetFrameRate = 30; // Lower framerate for web
            QualitySettings.shadows = ShadowQuality.Disable; // Disable shadows
            
        #endif
    }
}
\`\`\`
      `,
      learningObjectives: [
        'Profile and analyze game performance',
        'Implement object pooling systems',
        'Optimize textures and assets',
        'Configure platform-specific settings'
      ]
    }
  ]
};